// Telegram WebApp SDK Integration Helper

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        isExpanded: boolean;
        viewportHeight: number;
        viewportStableHeight: number;
        headerColor: string;
        backgroundColor: string;
        setHeaderColor: (color: string) => void;
        setBackgroundColor: (color: string) => void;
        enableClosingConfirmation: () => void;
        initData?: string;
        BackButton: {
          isVisible: boolean;
          show: () => void;
          hide: () => void;
          onClick: (cb: () => void) => void;
          offClick: (cb: () => void) => void;
        };
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
          notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
          selectionChanged: () => void;
        };
        initDataUnsafe?: {
          start_param?: string;
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            language_code?: string;
            photo_url?: string;
          };
        };
      };
    };
  }
}

import { TelegramUserProfile } from '../types';

const ANON_TOKEN_KEY = 'musictv_anon_client_token_v1';

function generateAnonToken(): string {
  const bytes = new Uint8Array(16);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return `anon_${hex}`;
}

export const tg = {
  _hapticsEnabled: true,

  setHapticsEnabled(enabled: boolean): void {
    this._hapticsEnabled = enabled;
  },

  isHapticsEnabled(): boolean {
    return this._hapticsEnabled;
  },

  isAvailable(): boolean {
    return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
  },

  init(): void {
    if (!this.isAvailable()) return;
    try {
      const webapp = window.Telegram!.WebApp!;
      webapp.ready();
      webapp.expand();
      webapp.enableClosingConfirmation();
      webapp.setHeaderColor('#0c0e12');
      webapp.setBackgroundColor('#0c0e12');
    } catch (e) {
      console.warn('Telegram WebApp init error:', e);
    }
  },

  haptic(style: 'light' | 'medium' | 'heavy' | 'success' | 'error' = 'light'): void {
    if (!this._hapticsEnabled) return;
    if (!this.isAvailable()) return;
    try {
      const haptic = window.Telegram!.WebApp!.HapticFeedback;
      if (!haptic) return;
      if (style === 'success' || style === 'error') {
        haptic.notificationOccurred(style);
      } else {
        haptic.impactOccurred(style);
      }
    } catch {
      // Ignore haptic errors in browser preview
    }
  },

  showAlert(message: string, callback?: () => void): void {
    if (this.isAvailable() && (window.Telegram?.WebApp as any)?.showAlert) {
      (window.Telegram?.WebApp as any).showAlert(message, callback);
    } else {
      window.alert(message);
      if (callback) callback();
    }
  },

  setThemeColors(bg: string, header: string): void {
    if (!this.isAvailable()) return;
    try {
      const webapp = window.Telegram!.WebApp!;
      webapp.setBackgroundColor(bg);
      webapp.setHeaderColor(header);
    } catch {
      // Ignore in non-TG environment
    }
  },

  getUser() {
    if (!this.isAvailable()) return null;
    return window.Telegram?.WebApp?.initDataUnsafe?.user || null;
  },

  getInitData(): string {
    if (!this.isAvailable()) return '';
    return window.Telegram?.WebApp?.initData || '';
  },

  getOrCreateAnonToken(): string {
    if (typeof window === 'undefined') return 'anon_00000000000000000000000000000000';
    try {
      let token = localStorage.getItem(ANON_TOKEN_KEY);
      if (!token || !/^anon_[a-f0-9]{32}$/.test(token)) {
        token = generateAnonToken();
        localStorage.setItem(ANON_TOKEN_KEY, token);
      }
      return token;
    } catch {
      return generateAnonToken();
    }
  },

  /**
   * Returns an isolated storage namespace string unique to the current Telegram user
   * (or unique to the current browser instance if opened outside Telegram).
   */
  getUserScope(): string {
    const rawUser = this.getUser();
    if (rawUser && rawUser.id) {
      return `tg_${rawUser.id}`;
    }
    const anonToken = this.getOrCreateAnonToken();
    return `web_${anonToken.slice(5, 21)}`;
  },

  /**
   * Returns authentication headers for isolated server-side state sync.
   */
  getAuthHeaders(): Record<string, string> {
    const initData = this.getInitData();
    if (initData) {
      return {
        'X-Telegram-Init-Data': initData,
      };
    }
    return {
      'X-Client-Token': this.getOrCreateAnonToken(),
    };
  },

  getUserProfile(): TelegramUserProfile {
    const rawUser = this.getUser();
    if (rawUser && rawUser.id) {
      const name = [rawUser.first_name, rawUser.last_name].filter(Boolean).join(' ');
      return {
        id: rawUser.id,
        firstName: name || 'Слушатель',
        lastName: rawUser.last_name,
        username: rawUser.username || `user_${rawUser.id}`,
        photoUrl: rawUser.photo_url || '',
      };
    }
    const anonToken = this.getOrCreateAnonToken();
    const shortHex = anonToken.slice(5, 11);
    const derivedId = parseInt(shortHex, 16) || 100000;
    return {
      id: derivedId,
      firstName: 'Гость студии',
      username: `guest_${shortHex}`,
      photoUrl: '',
    };
  },

  onBackClick(cb: () => void): () => void {
    if (!this.isAvailable()) return () => {};
    const btn = window.Telegram!.WebApp!.BackButton;
    btn.show();
    btn.onClick(cb);
    return () => {
      btn.offClick(cb);
      btn.hide();
    };
  },
};
